import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { Box, Button, Divider, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import type {
  QIT,
  QuestionGroup,
} from 'src/components/WSCertificationSummary/types/QuestionnaireSchemaTypes';
import type { QuestionnaireAnswersMap } from 'src/services/RenterQuestionnaire.service';
import { deleteAnswerGroup } from 'src/services/RenterQuestionnaire.service';
import QuestionnaireQuestion from './QuestionnaireQuestion';

interface QuestionnaireGroupProps {
  group: QuestionGroup;
  qits: QIT[];
  sectionHeaderHeight: number;
}

function getAnswerGroupIds(
  qits: QIT[],
  answers: QuestionnaireAnswersMap,
  surveyId: number
): number[] {
  const groupIds = new Set<number>();
  for (const qit of qits) {
    const qitAnswers = answers[qit.id];
    if (!qitAnswers) {
      continue;
    }
    const surveyAnswers = qitAnswers[surveyId];
    if (!surveyAnswers) {
      continue;
    }
    for (const agId of Object.keys(surveyAnswers)) {
      groupIds.add(Number(agId));
    }
  }
  return [...groupIds].sort((a, b) => a - b);
}

function isChoiceSelectedInGroup(
  answers: QuestionnaireAnswersMap,
  parentQitId: number,
  parentChoiceId: number,
  surveyId: number,
  answerGroupIds: number[]
): boolean {
  const qitAnswers = answers[parentQitId];
  if (!qitAnswers) {
    return false;
  }
  const surveyAnswers = qitAnswers[surveyId];
  if (!surveyAnswers) {
    return false;
  }
  for (const agId of answerGroupIds) {
    const answer = surveyAnswers[agId];
    if (answer?.question_choices?.some((c) => c.id === parentChoiceId)) {
      return true;
    }
  }
  return false;
}

const QuestionnaireGroup: FC<QuestionnaireGroupProps> = ({
  group,
  qits,
  sectionHeaderHeight,
}) => {
  const { certificationId, surveyId } = useParams<{
    certificationId: string;
    surveyId: string;
  }>();
  const certId = certificationId ? Number(certificationId) : NaN;
  const activeSurveyId = surveyId ? Number(surveyId) : 0;

  const toolbarOffset = useSelector(
    (state: RootState) => state.newRenterUI.toolbarOffset
  );

  const groupNameTop = sectionHeaderHeight + toolbarOffset;

  const answers = useSelector(
    (state: RootState) => state.newRenterInterface.answers[certId]
  ) as QuestionnaireAnswersMap | undefined;

  const visibility = useSelector(
    (state: RootState) =>
      state.newRenterInterface.questionnaireVisibility[certId]
  );

  const filteredQitIds = useMemo(
    () => new Set(visibility?.filteredQitIds ?? []),
    [visibility?.filteredQitIds]
  );

  const newlyRevealed = useMemo(
    () => new Set(visibility?.newlyRevealed ?? []),
    [visibility?.newlyRevealed]
  );

  const revealOrigin = useMemo(
    () =>
      new Map(
        Object.entries(visibility?.revealOrigin ?? {}).map(([k, v]) => [
          Number(k),
          v,
        ])
      ),
    [visibility?.revealOrigin]
  );

  const isMulti = group.type === 'multi';

  const answerGroupIds = useMemo(
    () => getAnswerGroupIds(qits, answers ?? {}, activeSurveyId),
    [qits, answers, activeSurveyId]
  );

  const effectiveGroups = useMemo(() => {
    if (isMulti) {
      return answerGroupIds.length > 0 ? answerGroupIds : [0];
    }
    return answerGroupIds.length > 0 ? [answerGroupIds[0]] : [0];
  }, [isMulti, answerGroupIds]);

  const [deletingGroup, setDeletingGroup] = useState<number | null>(null);

  const handleRemoveGroup = useCallback(async (answerGroupId: number) => {
    if (answerGroupId === 0) {
      return;
    }
    setDeletingGroup(answerGroupId);
    try {
      await deleteAnswerGroup(answerGroupId);
    } catch {
      // UI updates via WebSocket
    } finally {
      setDeletingGroup(null);
    }
  }, []);

  const visibleQits = useMemo(
    () => qits.filter((qit) => filteredQitIds.has(qit.id)),
    [qits, filteredQitIds]
  );

  const scopedVisibleQitsByGroup = useMemo(() => {
    if (!isMulti) {
      return null;
    }
    const currentAnswers = answers ?? {};
    const result = new Map<number, QIT[]>();
    for (const agId of effectiveGroups) {
      const scopedQits = visibleQits.filter((qit) => {
        if (!qit.parent_qit_id || !qit.parent_choice_id) {
          return true;
        }
        return isChoiceSelectedInGroup(
          currentAnswers,
          qit.parent_qit_id,
          qit.parent_choice_id,
          activeSurveyId,
          [agId]
        );
      });
      result.set(agId, scopedQits);
    }
    return result;
  }, [isMulti, effectiveGroups, visibleQits, answers, activeSurveyId]);

  if (visibleQits.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 2 }}>
      {group.name && (
        <Box
          sx={{
            position: 'sticky',
            top: groupNameTop,
            zIndex: 2,
            backgroundColor: 'background.paper',
            py: 0.5,
            px: 2.5,
            transition: 'top 0.25s ease-in-out',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {group.name}
          </Typography>
        </Box>
      )}

      {effectiveGroups.map((agId, idx) => {
        const qitsForGroup = isMulti
          ? scopedVisibleQitsByGroup?.get(agId) ?? visibleQits
          : visibleQits;

        if (qitsForGroup.length === 0) {
          return null;
        }

        return (
          <Box
            key={agId}
            sx={{
              mb: 1.5,
              ...(isMulti &&
                effectiveGroups.length > 1 && {
                  pl: 2,
                  borderLeft: 3,
                  borderColor: 'divider',
                  backgroundColor:
                    idx % 2 === 1 ? 'action.hover' : 'transparent',
                  borderRadius: 1,
                }),
            }}
          >
            {isMulti && effectiveGroups.length > 1 && (
              <Typography
                variant="caption"
                fontWeight={600}
                sx={{ display: 'block', pt: 1, pb: 0.5, px: 1 }}
              >
                {group.name || 'Group'} #{idx + 1}
              </Typography>
            )}

            {qitsForGroup.map((qit) => (
              <QuestionnaireQuestion
                key={`${qit.id}-${agId}`}
                qit={qit}
                surveyId={activeSurveyId}
                answerGroupId={agId || undefined}
                isNewlyRevealed={newlyRevealed.has(qit.id)}
                revealOrigin={revealOrigin.get(qit.id) ?? null}
              />
            ))}

            {isMulti && agId !== 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  px: 1,
                  pb: 1,
                }}
              >
                <Button
                  size="small"
                  color="error"
                  startIcon={<RemoveIcon />}
                  onClick={() => {
                    handleRemoveGroup(agId);
                  }}
                  disabled={deletingGroup === agId}
                >
                  <span>{deletingGroup === agId ? 'Removing…' : 'Remove'}</span>
                </Button>
              </Box>
            )}

            {isMulti && idx < effectiveGroups.length - 1 && (
              <Divider sx={{ my: 1 }} />
            )}
          </Box>
        );
      })}

      {isMulti && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            py: 1,
            px: 2.5,
          }}
        >
          <Button size="small" startIcon={<AddIcon />} variant="outlined">
            <span>Add {group.name || 'Group'}</span>
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default QuestionnaireGroup;
